import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { X, QrCode, Download, Copy, CheckCircle, Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { walletApi } from '@/services/api'
import { Input } from '@/components/ui/Input'
import { Button } from '@/components/ui/Button'
import toast from 'react-hot-toast'
import { useWallets } from '@/hooks/useWallets'
import { formatCurrency } from '@/lib/utils'
import QRCodeGenerator from 'qrcode'

interface ReceiveMoneyModalProps {
  isOpen: boolean
  onClose: () => void
  onSuccess?: () => void
}

interface ReceiveQRCode {
  id?: string
  qr_code_data?: string
  qr_code_hash: string
  qr_image_url?: string
  amount: number
  currency: string
  wallet_id: string
  expires_at?: string
}

export function ReceiveMoneyModal({ isOpen, onClose, onSuccess }: ReceiveMoneyModalProps) {
  const { t } = useTranslation()
  const { wallets } = useWallets()
  const [amount, setAmount] = useState<string>('')
  const [selectedWalletId, setSelectedWalletId] = useState<string>('')
  const [description, setDescription] = useState<string>('')
  const [qrCode, setQrCode] = useState<ReceiveQRCode | null>(null)
  const [qrImageDataUrl, setQrImageDataUrl] = useState<string>('')
  const [isGenerating, setIsGenerating] = useState(false)
  const [isCopied, setIsCopied] = useState(false)
  const [timeRemaining, setTimeRemaining] = useState<string>('')

  useEffect(() => {
    if (isOpen) {
      // Reset state when modal opens
      setAmount('')
      setDescription('')
      setQrCode(null)
      setQrImageDataUrl('')
      setIsCopied(false)
      setTimeRemaining('')
      
      // Set default wallet
      const defaultWallet = wallets?.find(w => w.is_default) || wallets?.[0]
      if (defaultWallet) {
        setSelectedWalletId(defaultWallet.id)
      }
    }
  }, [isOpen, wallets])

  // Countdown timer effect
  useEffect(() => {
    if (!qrCode?.expires_at) return

    const updateCountdown = () => {
      const now = new Date().getTime()
      const expiry = new Date(qrCode.expires_at!).getTime()
      const diff = expiry - now

      if (diff <= 0) {
        setTimeRemaining(t('wallet.expired') || 'Expirado')
        return
      }

      const minutes = Math.floor(diff / 60000)
      const seconds = Math.floor((diff % 60000) / 1000)

      if (minutes > 0) {
        setTimeRemaining(`${minutes}m ${seconds}s`)
      } else {
        setTimeRemaining(`${seconds}s`)
      }
    }

    updateCountdown()
    const interval = setInterval(updateCountdown, 1000)

    return () => clearInterval(interval)
  }, [qrCode?.expires_at, t])

  const handleGenerateQR = async () => {
    if (!amount || parseFloat(amount) <= 0) {
      toast.error(t('wallet.pleaseEnterAmount') || 'Por favor, insira um valor')
      return
    }

    if (!selectedWalletId) {
      toast.error(t('wallet.selectWallet') || 'Por favor, selecione uma carteira')
      return
    }

    setIsGenerating(true)

    try {
      // Generate QR code for receiving money
      const response = await walletApi.generateReceiveQR({
        wallet_id: selectedWalletId,
        amount: parseFloat(amount),
        description: description || t('wallet.receiveMoney') || 'Receber dinheiro',
      })

      const data = response.data?.data || response.data

      // Response should only contain qr_code_hash and expires_at
      const qrHash = data.qr_code_hash
      const expiresAt = data.expires_at
      
      if (!qrHash) {
        throw new Error('QR code hash not received from server')
      }

      // Store QR code data with hash and expiry
      setQrCode({
        qr_code_hash: qrHash,
        expires_at: expiresAt,
        amount: parseFloat(amount),
        currency: selectedWallet?.currency || 'STN',
        wallet_id: selectedWalletId,
      })

      try {
        // Generate QR code image from hash only
        const qrDataUrl = await QRCodeGenerator.toDataURL(qrHash, {
          width: 300,
          margin: 2,
        })
        setQrImageDataUrl(qrDataUrl)
      } catch (qrError) {
        console.error('Error generating QR image:', qrError)
        toast.error(t('wallet.qrGenerationError') || 'Erro ao gerar imagem do QR code')
      }
    } catch (error: any) {
      console.error('Error generating receive QR code:', error)
      const errorMessage = error.response?.data?.error || 
                          error.response?.data?.detail ||
                          t('wallet.qrGenerationError') || 
                          'Erro ao gerar QR code'
      toast.error(errorMessage)
    } finally {
      setIsGenerating(false)
    }
  }

  const handleCopyQRData = () => {
    // Copy the hash only - the scanner will lookup the data from backend
    if (qrCode?.qr_code_hash) {
      navigator.clipboard.writeText(qrCode.qr_code_hash)
      setIsCopied(true)
      toast.success(t('wallet.qrDataCopied') || 'Código QR copiado!')
      setTimeout(() => setIsCopied(false), 2000)
    }
  }

  const selectedWallet = wallets?.find(w => w.id === selectedWalletId)

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/50 z-[100]"
          />

          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: 20 }}
            className="fixed inset-0 z-[100] flex items-center justify-center p-4 pb-20 md:pb-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-white rounded-2xl shadow-xl max-w-md w-full max-h-[calc(90vh-5rem)] md:max-h-[90vh] overflow-y-auto">
              <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
                <h2 className="text-xl font-bold text-text-primary">
                  {t('wallet.receiveMoney') || 'Receber Dinheiro'}
                </h2>
                <button
                  onClick={onClose}
                  className="p-2 rounded-full hover:bg-gray-100 transition-colors"
                  disabled={isGenerating}
                >
                  <X className="h-5 w-5 text-text-secondary" />
                </button>
              </div>

              <div className="p-6 space-y-6">
                {!qrCode ? (
                  <>
                    {/* Wallet Selection */}
                    {wallets && wallets.length > 1 && (
                      <div>
                        <label className="block text-sm font-medium text-text-primary mb-2">
                          {t('wallet.selectWallet') || 'Selecionar Carteira'}
                        </label>
                        <select
                          value={selectedWalletId}
                          onChange={(e) => setSelectedWalletId(e.target.value)}
                          className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary outline-none"
                          disabled={isGenerating}
                        >
                          {wallets.map((wallet) => (
                            <option key={wallet.id} value={wallet.id}>
                              {wallet.currency_code || wallet.currency} - {formatCurrency(wallet.balance, wallet.currency_code || wallet.currency)}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}

                    {/* Amount Input */}
                    <div>
                      <label className="block text-sm font-medium text-text-primary mb-2">
                        {t('wallet.amount') || 'Valor a Receber'}
                      </label>
                      <div className="relative">
                        <Input
                          type="number"
                          step="0.01"
                          min="0.01"
                          placeholder="0.00"
                          value={amount}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setAmount(e.target.value)}
                          required
                          disabled={isGenerating}
                          className="pr-20"
                        />
                        <span className="absolute right-4 top-1/2 -translate-y-1/2 text-text-secondary">
                          {selectedWallet?.currency === 'STN' ? 'Db' : selectedWallet?.currency === 'EUR' ? '€' : '$'}
                        </span>
                      </div>
                    </div>

                    {/* Description Input */}
                    <div>
                      <label className="block text-sm font-medium text-text-primary mb-2">
                        {t('common.description')} ({t('common.optional')})
                      </label>
                      <Input
                        type="text"
                        placeholder={t('wallet.receiveDescription') || 'Descrição do pagamento'}
                        value={description}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDescription(e.target.value)}
                        disabled={isGenerating}
                      />
                    </div>

                    {/* Generate QR Button */}
                    <Button
                      type="button"
                      onClick={handleGenerateQR}
                      className="w-full"
                      disabled={isGenerating || !amount || parseFloat(amount) <= 0 || !selectedWalletId}
                    >
                      {isGenerating ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin mr-2" />
                          {t('wallet.generatingQR') || 'A gerar QR Code...'}
                        </>
                      ) : (
                        <>
                          <QrCode className="h-4 w-4 mr-2" />
                          {t('wallet.generateQR') || 'Gerar QR Code'}
                        </>
                      )}
                    </Button>
                  </>
                ) : (
                  <>
                    {/* QR Code Display */}
                    <div className="text-center space-y-4">
                      <div className="bg-gray-50 rounded-lg p-6">
                        <div className="flex items-center justify-center mb-4">
                          {qrImageDataUrl ? (
                            <img 
                              src={qrImageDataUrl} 
                              alt="QR Code" 
                              className="w-64 h-64 mx-auto"
                            />
                          ) : (
                            <div className="w-64 h-64 bg-gray-200 rounded-lg flex items-center justify-center">
                              <QrCode className="h-16 w-16 text-gray-400" />
                            </div>
                          )}
                        </div>
                        
                        <div className="space-y-2">
                          <p className="text-lg font-semibold text-text-primary">
                            {formatCurrency(parseFloat(amount), qrCode.currency || selectedWallet?.currency_code || selectedWallet?.currency || 'STN')}
                          </p>
                          {qrCode.expires_at && (
                            <div className="space-y-1">
                              <p className="text-sm text-text-secondary">
                                {t('wallet.expiresAt') || 'Expira em'} {new Date(qrCode.expires_at).toLocaleTimeString()}
                              </p>
                              <div className="flex items-center justify-center gap-2">
                                <p className="text-xs text-text-secondary">
                                  {t('wallet.qrCodeValidFor') || 'Válido por 5 minutos'}
                                </p>
                                {timeRemaining && (
                                  <p className={`text-xs font-semibold ${
                                    timeRemaining.includes('s') && !timeRemaining.includes('m') 
                                      ? 'text-error' 
                                      : 'text-primary'
                                  }`}>
                                    ({t('wallet.timeRemaining') || 'Restam'} {timeRemaining})
                                  </p>
                                )}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Copy QR Data */}
                      <Button
                        type="button"
                        variant="outline"
                        onClick={handleCopyQRData}
                        className="w-full"
                      >
                        {isCopied ? (
                          <>
                            <CheckCircle className="h-4 w-4 mr-2 text-success" />
                            {t('wallet.copied') || 'Copiado!'}
                          </>
                        ) : (
                          <>
                            <Copy className="h-4 w-4 mr-2" />
                            {t('wallet.copyQRData') || 'Copiar Código QR'}
                          </>
                        )}
                      </Button>

                      {/* Actions */}
                      <div className="flex space-x-4">
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => {
                            setQrCode(null)
                            setQrImageDataUrl('')
                            setAmount('')
                            setDescription('')
                          }}
                          className="flex-1"
                        >
                          {t('wallet.newQR') || 'Novo QR Code'}
                        </Button>
                        <Button
                          type="button"
                          onClick={onClose}
                          className="flex-1"
                        >
                          {t('common.close') || 'Fechar'}
                        </Button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  )
}

